<div class="call-progress-container" [class.minimized]="isMinimized">
  <!-- Drag handle header -->
  <div class="drag-handle" (mousedown)="startDragging($event)" [class.dragging]="isDragging">
    <div class="drag-icon">
      <mat-icon>drag_indicator</mat-icon>
    </div>
    <h1 class="call-title" *ngIf="!isMinimized">
      {{ additionalParticipantCallSid ? 'Conference Call' : 'Call In Progress' }}
    </h1>
    <div class="header-actions">
      <button
        mat-icon-button
        (click)="toggleMinimize(); $event.stopPropagation()"
        (mousedown)="$event.stopPropagation()"
        [matTooltip]="isMinimized ? 'Expand' : 'Minimize'"
        class="minimize-button"
      >
        <mat-icon>{{ isMinimized ? 'open_in_full' : 'minimize' }}</mat-icon>
      </button>
    </div>
  </div>

  <!-- Maximized content -->
  <div *ngIf="!isMinimized" class="maximized-content">
    <div mat-dialog-content class="call-content">
      <div class="caller-info">
        <mat-icon class="call-icon">
          {{ additionalParticipantCallSid ? 'groups' : 'phone_in_talk' }}
        </mat-icon>
        <p class="caller-name" *ngIf="data.callerName">{{ data.callerName }}</p>
        <p class="caller-number">{{ formattedCallerNumber }}</p>
        <p class="patient-match" *ngIf="matchedPatient">
          <mat-icon class="patient-match-icon">verified_user</mat-icon>
          {{ matchedPatient.firstName }} {{ matchedPatient.lastName }}
        </p>
        <ng-container *ngIf="currentUser?.enable_conference_call">
          <div class="participant-badge" *ngIf="additionalParticipantCallSid">
            <div class="participant-info">
              <mat-icon>person_add</mat-icon>
              <span>{{ additionalParticipantNumber }}</span>
            </div>
            <button mat-icon-button class="hangup-button" (click)="hangUpParticipant()" matTooltip="Hang up">
              <mat-icon>call_end</mat-icon>
            </button>
          </div>
        </ng-container>
      </div>

      <div class="timer-container">
        <mat-icon class="timer-icon" color="primary">schedule</mat-icon>
        <p class="timer">{{ durationFormatted }}</p>
        <app-call-signal-indicator [quality]="callQuality"></app-call-signal-indicator>
      </div>

      <div class="call-dial">
        <app-dial (digit)="sendDigits($event)" (panelToggled)="onKeypadToggled($event)"></app-dial>
      </div>
    </div>

    <div mat-dialog-actions class="call-actions">
      <ng-container *ngIf="currentUser?.enable_conference_call">
        <button
          mat-fab
          color="primary"
          class="add-participant-button"
          matTooltip="Add participant"
          (click)="toggleAddParticipant()"
          *ngIf="!additionalParticipantCallSid"
        >
          <mat-icon>person_add</mat-icon>
        </button>
      </ng-container>
      <ng-container *ngIf="currentUser?.enable_conference_call && parentCallSid">
        <button
          mat-fab
          [color]="isOnHold ? 'accent' : 'primary'"
          class="hold-button"
          [attr.aria-disabled]="holdPending"
          [attr.aria-busy]="holdPending"
          (click)="toggleHold()"
          [attr.aria-label]="isOnHold ? 'Resume call' : 'Put call on hold'"
          [attr.aria-pressed]="isOnHold"
          [matTooltip]="isOnHold ? 'Resume' : 'Hold'"
        >
          <mat-spinner *ngIf="holdPending" [diameter]="20"></mat-spinner>
          <mat-icon *ngIf="!holdPending">{{ isOnHold ? 'play_arrow' : 'pause' }}</mat-icon>
        </button>
      </ng-container>
      <button
        mat-fab
        [color]="isMuted ? 'warn' : 'primary'"
        class="mute-button"
        (click)="toggleMute()"
        [matTooltip]="isMuted ? 'Unmute' : 'Mute'"
      >
        <mat-icon>{{ isMuted ? 'mic_off' : 'mic' }}</mat-icon>
      </button>
      <button
        mat-fab
        color="accent"
        class="go-to-chart-button"
        *ngIf="matchedPatient && (data.isPostDischargeCall || (chartDraftEnabled$ | async))"
        (click)="goToPatientChart()"
        matTooltip="Go to patient chart"
      >
        <mat-icon>assignment_ind</mat-icon>
      </button>
      <button mat-fab class="hangup-button" (click)="onHangUp()" matTooltip="Hang Up">
        <mat-icon>call_end</mat-icon>
      </button>
    </div>
  </div>

  <!-- Minimized content - horizontal compact layout -->
  <div *ngIf="isMinimized" class="minimized-content">
    <div class="minimized-info">
      <mat-icon class="mini-icon" color="primary">phone_in_talk</mat-icon>
      <span class="mini-timer">{{ durationFormatted }}</span>
      <app-call-signal-indicator [quality]="callQuality" [compact]="true"></app-call-signal-indicator>
    </div>
    <div class="minimized-actions">
      <ng-container *ngIf="currentUser?.enable_conference_call">
        <button
          mat-fab
          matTooltip="Add participant"
          class="mini-add-participant-button"
          color="primary"
          (click)="toggleAddParticipant()"
          *ngIf="!additionalParticipantCallSid"
        >
          <mat-icon>person_add</mat-icon>
        </button>
      </ng-container>

      <ng-container *ngIf="currentUser?.enable_conference_call && parentCallSid">
        <button
          mat-fab
          [color]="isOnHold ? 'accent' : 'primary'"
          class="mini-hold-button"
          [attr.aria-disabled]="holdPending"
          [attr.aria-busy]="holdPending"
          (click)="toggleHold()"
          [attr.aria-label]="isOnHold ? 'Resume call' : 'Put call on hold'"
          [attr.aria-pressed]="isOnHold"
          [matTooltip]="isOnHold ? 'Resume' : 'Hold'"
        >
          <mat-spinner *ngIf="holdPending" [diameter]="20"></mat-spinner>
          <mat-icon *ngIf="!holdPending">{{ isOnHold ? 'play_arrow' : 'pause' }}</mat-icon>
        </button>
      </ng-container>

      <button
        mat-fab
        [color]="isMuted ? 'warn' : 'primary'"
        (click)="toggleMute()"
        [matTooltip]="isMuted ? 'Unmute' : 'Mute'"
        class="mini-mute-button"
      >
        <mat-icon>{{ isMuted ? 'mic_off' : 'mic' }}</mat-icon>
      </button>
      <button
        mat-fab
        color="accent"
        class="mini-go-to-chart-button"
        *ngIf="matchedPatient && (data.isPostDischargeCall || (chartDraftEnabled$ | async))"
        (click)="goToPatientChart()"
        matTooltip="Go to patient chart"
      >
        <mat-icon>assignment_ind</mat-icon>
      </button>
      <button mat-fab (click)="onHangUp()" matTooltip="Hang Up" class="mini-hangup-button">
        <mat-icon>call_end</mat-icon>
      </button>
    </div>
  </div>

  <div class="add-participant-container" [class.show]="showAddParticipant">
    <mat-form-field appearance="outline" class="participant-input">
      <mat-label>Add participant</mat-label>
      <input
        matInput
        type="tel"
        [formControl]="phoneNumberControl"
        placeholder="(555) 123-4567"
        inputmode="tel"
        (blur)="formatPhoneNumber(phoneNumberControl)"
        (keydown.enter)="handleEnterKey($event)"
        aria-label="Phone number to add as participant"
      />
      <mat-hint>Enter a 10-digit phone number or 1-800 number</mat-hint>
      <mat-error *ngIf="phoneNumberControl.hasError('required')"> Phone number is required </mat-error>
      <mat-error *ngIf="phoneNumberControl.hasError('invalidPhone')">
        Please enter a valid 10-digit phone number or 1-800 number
      </mat-error>
    </mat-form-field>
    <button
      mat-raised-button
      color="primary"
      class="add-button"
      [disabled]="!phoneNumberControl.valid"
      (click)="addParticipant()"
    >
      <mat-icon>add</mat-icon>
      <span>Add</span>
    </button>
  </div>

  <!-- Microphone not reaching the caller: kept on screen for as long as it lasts, unlike a snackbar -->
  <div *ngIf="microphoneWarning" class="microphone-alert" role="alert" aria-live="assertive">
    <mat-icon class="microphone-alert__icon">mic_off</mat-icon>
    <span class="microphone-alert__message">{{ microphoneWarning }}</span>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <mat-icon class="error-icon">error_outline</mat-icon>
    <span>{{ errorMessage }}</span>
  </div>
</div>
